import React, { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import Alert from './Alert';
import { Contact, formatPhoneNumber, SearchSelect } from "./FieldComponents";
import { CreateContact } from "./CreateContact";
import '../styles/Leads.css';
import { Star, ChevronDown, Smile, Meh, Frown, SmilePlus, LayoutList } from "lucide-react";
import { StarOutlineSharp, StarRate, StarRateOutlined, StarRateSharp, StarSharp, TheaterComedyTwoTone } from "@mui/icons-material";
import { CreateLead } from "./CreateLead";
import { useNavigate } from 'react-router';
import { SearchBar } from './Nav';
import { useNavVariant } from '../App';

export const Leads = ({ user }) => {
    const [leads, setLeads] = useState([]);
    const [createLead, setCreateLead] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const navVariant = useNavVariant();
    const [displayHeaders, setDisplayHeaders] = useState({
        assigned_to: true,
        assigned_to_attorney: true,
        created_at: true,
        case_type: true,
        marketing_source: true,
        case_likelihood: true,
        office: true
    });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [perPageOptions] = useState([25, 50, 100, 500, 1000]);
    const [showNatureModal, setShowNatureModal] = useState(false);
    const [selectedNature, setSelectedNature] = useState(null);
    const [natureDescription, setNatureDescription] = useState('');
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [message, setMessage] = useState({ message: '', type: '' });
    const [filterOptions, setFilterOptions] = useState({
        assigned_to: [],
        assigned_to_attorney: [],
        marketing_source: [],
        marketing_lead: [],
        statuses: []
    });
    const [filters, setFilters] = useState({
        assigned_to: [],
        assigned_to_attorney: [],
        marketing_source: [],
        marketing_lead: [],
        statuses: [],
        referral_contacts: []
    });
    const [referralContactOptions, setReferralContactOptions] = useState({});
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);

    const caseLikelihood = ["No Case", "Unlikely Case", "Possible Case", "Likely Case", "Very Likely Case"];

    const handleOpenNatureModal = (e, contactId, nature, description) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedContactId(contactId);
        setSelectedNature(nature || null);
        setNatureDescription(description || '');
        setShowNatureModal(true);
    };

    const handleSaveNature = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    contact_id: selectedContactId,
                    nature: selectedNature,
                    nature_description: natureDescription
                })
            });

            const data = await response.json();
            if (data.success) {
                setShowNatureModal(false);
                fetchLeads(pagination.current_page, pagination.per_page);
            } else {
                console.error('Failed to update nature');
            }
        } catch (error) {
            console.error('Error updating nature:', error);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/leads.php?filter_options=true&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success && data.filter_options) {
                setFilterOptions(data.filter_options);
            }
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    };

    const fetchReferralContacts = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php?list=true', { credentials: 'include' });
            const data = await response.json();
            if (data.success && Array.isArray(data.contacts)) {
                const options = data.contacts.reduce((acc, contact) => {
                    if (contact?.id && contact?.name) {
                        acc[String(contact.id)] = contact.name;
                    }
                    return acc;
                }, {});
                setReferralContactOptions(options);
            }
        } catch (error) {
            console.error('Error fetching referral contacts:', error);
        }
    };

    const fetchLeads = async (page = 1, perPage = pagination.per_page) => {
        try {
            const timeFilter = params.get('time');
            const statusFilter = params.get('status');
            const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const timeParam = timeFilter ? `&time=${timeFilter}` : '';
            const statusParam = statusFilter ? `&status=${statusFilter}` : '';
            const paginationParams = `&page=${page}&per_page=${perPage}`;
            
            // Add filter parameters
            const filterParams = [];
            if (filters.assigned_to.length > 0) {
                filterParams.push(`assigned_to=${filters.assigned_to.join(',')}`);
            }
            if (filters.assigned_to_attorney.length > 0) {
                filterParams.push(`assigned_to_attorney=${filters.assigned_to_attorney.join(',')}`);
            }
            if (filters.marketing_source.length > 0) {
                filterParams.push(`marketing_source=${filters.marketing_source.join(',')}`);
            }
            if (filters.marketing_lead.length > 0) {
                filterParams.push(`marketing_lead=${filters.marketing_lead.join(',')}`);
            }
            if (filters.statuses.length > 0) {
                filterParams.push(`statuses=${filters.statuses.join(',')}`);
            }
            if (filters.referral_contacts.length > 0) {
                filterParams.push(`referral_contacts=${filters.referral_contacts.join(',')}`);
            }
            const filterString = filterParams.length > 0 ? '&' + filterParams.join('&') : '';
            
            const response = await fetch(`https://api.casedb.co/leads.php?time=${new Date().getTime()}${timeParam}${statusParam}${searchParam}${paginationParams}${filterString}`, { credentials: 'include' });
            const data = await response.json();
            setLeads(data.leads || []);
            if (data.pagination) {
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error("Error fetching leads:", error);
        }
    };

    useEffect(() => {
        if (new URLSearchParams(window.location.search).get("new") === "true") {
            setCreateLead(true);
        }
    }, []);

    const getDaysSinceCreation = (createdAt) => {
        if (!createdAt) return "N/A";
    
        const createdDate = new Date(createdAt);
        const currentDate = new Date();
        const timeDiff = currentDate - createdDate;
        const daysSince = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) < 0 ? 0 : Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
        switch (daysSince) {
            case 0:
                return "<1 day ago";
            case 1:
                return "1 day ago";
            default:
                break;
        }
        return `${daysSince} days ago`;
    };

    useEffect(() => {
        const updateDisplayHeaders = () => {
            const width = window.innerWidth;

            setDisplayHeaders({
                name: true,
                status: true,
                office: width > 1450,
                case_likelihood: width > 1250,
                marketing_source: width > 1100,
                assigned_to_attorney: width > 950,
                assigned_to: width > 800,
                created_at: width > 650,
                case_type: width > 500
            });
        };

        window.addEventListener("resize", updateDisplayHeaders);
        updateDisplayHeaders();

        document.title = "Leads | CaseDB"

        return () => window.removeEventListener("resize", updateDisplayHeaders);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchLeads(1, pagination.per_page);
        fetchFilterOptions();
        fetchReferralContacts();
    }, []);

    useEffect(() => {
        fetchLeads(pagination.current_page, pagination.per_page);
    }, [createLead]);

    useEffect(() => {
        fetchLeads(1, pagination.per_page);
    }, [params.get('time'), params.get('status')]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchLeads(1, pagination.per_page);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    useEffect(() => {
        fetchLeads(1, pagination.per_page);
    }, [filters]);

    const handleFilterChange = (filterType, value, checked) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            if (checked) {
                if (!newFilters[filterType].includes(value)) {
                    newFilters[filterType] = [...newFilters[filterType], value];
                }
            } else {
                newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
            }
            return newFilters;
        });
    };

    const clearAllFilters = () => {
        setFilters({
            assigned_to: [],
            assigned_to_attorney: [],
            marketing_source: [],
            marketing_lead: [],
            statuses: [],
            referral_contacts: []
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages) {
            fetchLeads(newPage, pagination.per_page);
        }
    };

    const handlePerPageChange = (newPerPage) => {
        fetchLeads(1, newPerPage);
    };

    const hasActiveFilters = () => {
        const filterArrays = [
            filters.assigned_to,
            filters.assigned_to_attorney,
            filters.marketing_source,
            filters.marketing_lead,
            filters.statuses,
            filters.referral_contacts
        ];

        const hasArrayFilters = filterArrays.some(arr => Array.isArray(arr) && arr.length > 0);
        const hasSearch = Boolean(searchQuery?.trim());
        const hasTimeFilter = Boolean(params.get('time'));
        const hasStatusFilter = Boolean(params.get('status'));

        return hasArrayFilters || hasSearch || hasTimeFilter || hasStatusFilter;
    };

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getLastMonthRange = () => {
        const now = new Date();
        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return [formatDate(firstDayLastMonth), formatDate(lastDayLastMonth)];
    };

    const buildReportCriteria = () => {
        const conditions = [];

        if (filters.assigned_to.length > 0) {
            conditions.push({
                field_id: 'leads.assigned_to',
                operator: '=',
                value: filters.assigned_to
            });
        }

        if (filters.assigned_to_attorney.length > 0) {
            conditions.push({
                field_id: 'leads.assigned_to_attorney',
                operator: '=',
                value: filters.assigned_to_attorney
            });
        }

        if (filters.marketing_source.length > 0) {
            conditions.push({
                field_id: 'leads.marketing_source',
                operator: '=',
                value: filters.marketing_source
            });
        }

        if (filters.marketing_lead.length > 0) {
            conditions.push({
                field_id: 'leads.marketing_lead',
                operator: '=',
                value: filters.marketing_lead
            });
        }

        if (filters.statuses.length > 0) {
            conditions.push({
                field_id: 'leads.status',
                operator: '=',
                value: filters.statuses
            });
        }

        if (filters.referral_contacts.length > 0) {
            conditions.push({
                field_id: 'leads.referral_contact',
                operator: '=',
                value: filters.referral_contacts
            });
        }

        if (searchQuery?.trim()) {
            conditions.push({
                logic: 'OR',
                conditions: [
                    {
                        field_id: 'contacts.full_name',
                        operator: 'LIKE',
                        value: searchQuery.trim()
                    },
                    {
                        field_id: 'contacts.nickname',
                        operator: 'LIKE',
                        value: searchQuery.trim()
                    }
                ]
            });
        }

        const timeFilter = params.get('time');
        if (timeFilter) {
            switch (timeFilter) {
                case 'today':
                    conditions.push({
                        field_id: 'leads.created_at',
                        operator: 'WITHIN',
                        value: 0
                    });
                    break;
                case 'this_month':
                    conditions.push({
                        field_id: 'leads.created_at',
                        operator: 'THIS_MONTH',
                        value: ''
                    });
                    break;
                case 'last_month': {
                    const [start, end] = getLastMonthRange();
                    conditions.push({
                        field_id: 'leads.created_at',
                        operator: 'BETWEEN',
                        value: [start, end]
                    });
                    break;
                }
                case 'this_year':
                    conditions.push({
                        field_id: 'leads.created_at',
                        operator: 'THIS_YEAR',
                        value: ''
                    });
                    break;
                default:
                    break;
            }
        }

        const statusFilter = params.get('status');
        if (statusFilter) {
            if (statusFilter === 'wanted') {
                conditions.push({
                    field_id: 'leads.status',
                    operator: '!=',
                    value: [11, 7]
                });
            } else {
                conditions.push({
                    field_id: 'leads.status',
                    operator: '=',
                    value: [statusFilter]
                });
            }
        }

        return {
            logic: 'AND',
            conditions
        };
    };

    const buildReportFields = () => ([
        'leads.id',
        'contacts.full_name',
        'leads.created_at',
        'leads.status',
        'leads.case_type_id',
        'leads.marketing_source',
        'leads.marketing_lead',
        'leads.referral_contact',
        'leads.assigned_to',
        'leads.assigned_to_attorney',
        'leads.case_likelihood',
        'leads.office'
    ]);

    const handleGenerateReport = async () => {
        try {
            const response = await fetch('https://api.casedb.co/report-update.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'save_as',
                    report_name: 'List of Leads - Generated Report',
                    report_type: 'leads',
                    fields: buildReportFields(),
                    criteria: buildReportCriteria()
                })
            });

            const data = await response.json();
            if (!data.success) {
                setMessage({ message: data.message || data.error || 'Failed to generate report', type: 'error' });
                console.error('Failed to generate report:', data.message || data.error);
            } else {
                setMessage({ message: 'Report generated successfully', type: 'success' });
            }
        } catch (error) {
            setMessage({ message: 'Error generating report', type: 'error' });
            console.error('Error generating report:', error);
        }
    };

    const filteredLeads = useMemo(() => {
        return (leads || []).filter(lead => Number(lead.deleted) !== 1);
    }, [leads]);

    const getTimeFilterDisplay = (timeFilter) => {
        switch (timeFilter) {
            case 'today':
                return 'Today';
            case 'this_month':
                return 'This Month';
            case 'last_month':
                return 'Last Month';
            case 'this_year':
                return 'This Year';
            default:
                return null;
        }
    };

    const getStatusFilterDisplay = (statusFilter) => {
        switch (statusFilter) {
            case 'wanted':
                return 'Wanted Status';
            default:
                return statusFilter ? `Status ${statusFilter}` : null;
        }
    };

    const timeFilter = params.get('time');
    const statusFilter = params.get('status');
    const timeFilterDisplay = getTimeFilterDisplay(timeFilter);
    const statusFilterDisplay = getStatusFilterDisplay(statusFilter);
    const combinedFilterDisplay = [timeFilterDisplay, statusFilterDisplay].filter(Boolean).join(' + ');
    const visibleLeadColumnCount = 2
        + (displayHeaders.assigned_to ? 1 : 0)
        + (displayHeaders.assigned_to_attorney ? 1 : 0)
        + (displayHeaders.created_at ? 1 : 0)
        + (displayHeaders.case_type ? 1 : 0)
        + (displayHeaders.marketing_source ? 1 : 0)
        + (displayHeaders.case_likelihood ? 1 : 0)
        + (displayHeaders.office ? 1 : 0);
    const leadNameColumnStyle = { width: screenWidth < 768 ? '55%' : `${Math.max(22, 100 / visibleLeadColumnCount)}%` };
    const leadStatusColumnStyle = { width: `${100 / visibleLeadColumnCount}%` };
    const leadColumnStyle = { width: `${100 / visibleLeadColumnCount}%` };

    return (
        <>
            <div className={`page-container leads-page ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
                <div id='page-header'>
                    <h2>
                        Leads
                    </h2>
                    <button className='action' onClick={() => setCreateLead(true)}>Create Lead</button>
                    <div className='active-cases subtext'>
                        <p>Leads: {pagination.total || filteredLeads.length}{combinedFilterDisplay ? ` (${combinedFilterDisplay})` : ''}</p> 
                    </div>
                </div>
                <div className='case-filters'>
                    <div className="filters-row">
                        <SearchBar 
                            expanded={true} 
                            setExpanded={() => {}} 
                            placeholder={`Search leads`}
                            setSearchQuery={setSearchQuery}
                        />
                        
                        {/* Per Page */}
                        {screenWidth >= 768 && (
                            <div className="filter-group">
                                <div className="select-wrapper">
                                    <select 
                                        className='filter-select' 
                                        value={pagination.per_page} 
                                        onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                    >
                                        {perPageOptions.map(option => (
                                            <option key={option} value={option}>{option} per page</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        )}

                        {screenWidth >= 768 && hasActiveFilters() && (
                            <div className='action tertiary' title="Generate Report with Active Filters" onClick={handleGenerateReport}>
                                <LayoutList size={20} />
                            </div>
                        )}
                    </div>

                    {/* Advanced Filters Row */}
                    {screenWidth >= 768 && <div className="filters-row advanced-filters">
                        {/* Assigned To Filter */}
                        <div className="filter-group">
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select' 
                                    value="" 
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value && !filters.assigned_to.includes(value)) {
                                            setFilters(prev => ({
                                                ...prev,
                                                assigned_to: [...prev.assigned_to, value]
                                            }));
                                        }
                                    }}
                                >
                                    <option value="">Assigned To</option>
                                    {filterOptions.assigned_to.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>

                        {/* Assigned Attorney Filter */}
                        <div className="filter-group">
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select'
                                    style={{ minWidth: '180px' }}
                                    value="" 
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value && !filters.assigned_to_attorney.includes(value)) {
                                            setFilters(prev => ({
                                                ...prev,
                                                assigned_to_attorney: [...prev.assigned_to_attorney, value]
                                            }));
                                        }
                                    }}
                                >
                                    <option value="">Assigned Attorney</option>
                                    {filterOptions.assigned_to_attorney.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>

                        {/* Marketing Source Filter */}
                        <div className="filter-group">
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select'
                                    style={{ minWidth: '180px' }}
                                    value="" 
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value && !filters.marketing_source.includes(value)) {
                                            setFilters(prev => ({
                                                ...prev,
                                                marketing_source: [...prev.marketing_source, value]
                                            }));
                                        }
                                    }}
                                >
                                    <option value="">Marketing Source</option>
                                    {filterOptions?.marketing_source?.map(source => (
                                        <option key={source?.id} value={source?.id}>
                                            {source?.source_name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>

                        {/* Marketing Lead Filter */}
                        <div className="filter-group">
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select'
                                    style={{ minWidth: '180px' }}
                                    value="" 
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value && !filters?.marketing_lead?.includes(value)) {
                                            setFilters(prev => ({
                                                ...prev,
                                                marketing_lead: [...(prev.marketing_lead || []), value]
                                            }));
                                        }
                                    }}
                                >
                                    <option value="">Marketing Lead</option>
                                    {filterOptions?.marketing_lead?.map(source => (
                                        <option key={source?.id} value={source?.id}>
                                            {source?.source_name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="filter-group">
                            <div className='select-wrapper'>
                                <select 
                                    className='filter-select'
                                    style={{ minWidth: '180px' }}
                                    value="" 
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value && !filters?.statuses?.includes(value)) {
                                            setFilters(prev => ({
                                                ...prev,
                                                statuses: [...(prev.statuses || []), value]
                                            }));
                                        }
                                    }}
                                >
                                    <option value="">Lead Status</option>
                                    {filterOptions?.statuses?.map(status => (
                                        <option key={status?.id} value={status?.id}>
                                            {status?.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>

                        {/* Referral Contact Filter */}
                        <div className="filter-group">
                            <SearchSelect
                                value={filters.referral_contacts[0] ?? null}
                                onChange={(value) => setFilters(prev => ({
                                    ...prev,
                                    referral_contacts: value ? [value] : []
                                }))}
                                options={referralContactOptions}
                                placeholder="Referral Contact"
                                small={true}
                            />
                        </div>
                    </div>}

                    {/* Active Filters Display */}
                    {screenWidth >= 768 && (filters.assigned_to.length > 0 || filters.assigned_to_attorney.length > 0 || filters.marketing_source.length > 0 || filters.marketing_lead.length > 0 || filters.statuses.length > 0 || filters.referral_contacts.length > 0) && (
                        <div className="active-filters-row">
                            {/* Assigned To Filters */}
                            {filters.assigned_to.map(userId => {
                                const user = filterOptions.assigned_to.find(u => u.id == userId);
                                return user ? (
                                    <div key={`assigned-to-${userId}`} className="filter-tag">
                                        Assigned: {user.name}
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => setFilters(prev => ({
                                                ...prev,
                                                assigned_to: prev.assigned_to.filter(id => id !== userId)
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}

                            {/* Assigned Attorney Filters */}
                            {filters.assigned_to_attorney.map(userId => {
                                const user = filterOptions.assigned_to_attorney.find(u => u.id == userId);
                                return user ? (
                                    <div key={`assigned-attorney-${userId}`} className="filter-tag">
                                        Attorney: {user.name}
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => setFilters(prev => ({
                                                ...prev,
                                                assigned_to_attorney: prev.assigned_to_attorney.filter(id => id !== userId)
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}

                            {/* Marketing Source Filters */}
                            {filters.marketing_source.map(sourceId => {
                                const source = filterOptions.marketing_source.find(s => s.id == sourceId);
                                return source ? (
                                    <div key={`marketing-source-${sourceId}`} className="filter-tag">
                                        Source: {source.source_name}
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => setFilters(prev => ({
                                                ...prev,
                                                marketing_source: prev.marketing_source.filter(id => id !== sourceId)
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}

                            {/* Marketing Lead Filters */}
                            {filters.marketing_lead.map(leadId => {
                                const lead = filterOptions.marketing_lead.find(s => s.id == leadId);
                                return lead ? (
                                    <div key={`marketing-lead-${leadId}`} className="filter-tag">
                                        Lead: {lead.source_name}
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => setFilters(prev => ({
                                                ...prev,
                                                marketing_lead: prev.marketing_lead.filter(id => id !== leadId)
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}

                            {/* Status Filters */}
                            {filters.statuses.map(statusId => {
                                const status = filterOptions.statuses.find(s => s.id == statusId);
                                return status ? (
                                    <div key={`status-${statusId}`} className="filter-tag">
                                        Status: {status.name}
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => setFilters(prev => ({
                                                ...prev,
                                                statuses: prev.statuses.filter(id => id !== statusId)
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}

                            {/* Referral Contact Filters */}
                            {filters.referral_contacts.map(contactId => {
                                const contactName = referralContactOptions?.[contactId];
                                return contactName ? (
                                    <div key={`referral-${contactId}`} className="filter-tag">
                                        Referral: {contactName}
                                        <button 
                                            className="filter-tag-remove"
                                            onClick={() => setFilters(prev => ({
                                                ...prev,
                                                referral_contacts: prev.referral_contacts.filter(id => id !== contactId)
                                            }))}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}

                            {/* Clear All Filters */}
                            <span onClick={() => {
                                clearAllFilters();
                            }}>
                                Clear filters
                            </span>
                        </div>
                    )}
                </div>
                
                {/* Pagination Controls */}
                <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: "10px", padding: "10px 0" }}>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_prev}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                        Previous
                    </button>
                    <span className='pagination-info subtext'>
                        Page {pagination.current_page} of {pagination.total_pages} ({pagination.total} total leads)
                    </span>
                    <button 
                        className='action alt' 
                        disabled={!pagination.has_next}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                        Next
                    </button>
                </div>
                <table className='leads-table default' style={{ tableLayout: 'fixed', width: '100%' }}>
                    <thead>
                        <tr className="default">
                            <th className='lead-name-td' style={leadNameColumnStyle}>Name</th>
                            {displayHeaders.assigned_to && <th style={leadColumnStyle}>Assigned To</th>}
                            {displayHeaders.assigned_to_attorney && <th style={leadColumnStyle}>Assigned Attorney</th>}
                            <th style={leadStatusColumnStyle}>Status</th>
                            {displayHeaders.created_at && <th style={leadColumnStyle}>Days Since Creation</th>}
                            {displayHeaders.case_type && <th style={leadColumnStyle}>Case Type</th>}
                            {displayHeaders.marketing_source && <th style={leadColumnStyle}>Marketing Source</th>}
                            {displayHeaders.case_likelihood && <th style={{ ...leadColumnStyle, textAlign: 'center' }}>Likelihood</th>}
                            {displayHeaders.office && <th style={leadColumnStyle}>Office</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLeads.length > 0 ? (
                            filteredLeads.map((lead, index) => (
                                <tr key={lead.id} className='lead default' onClick={() => navigate(`/intake/${lead.id}`)}>
                                    <td className='stacked lead-name-td' style={{ ...leadNameColumnStyle, display: 'flex', gap: '5px', flexDirection: 'row' }}>
                                        <div style={{ textWrap: 'wrap', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {lead.contact_name}{" "}
                                            </div>
                                            {displayHeaders.case_type && (
                                                <span className='subtext' style={{ textWrap: 'wrap' }}>
                                                    {
                                                        lead?.preferred_contact &&
                                                        lead?.detail_data &&
                                                        Object.keys(lead.detail_data).length > 0 &&
                                                        lead.detail_data[lead.preferred_contact]?.email 
                                                            ? `(${lead.detail_data[lead.preferred_contact]?.email})`
                                                            : lead.detail_data[lead.preferred_contact]?.number 
                                                                ? `${formatPhoneNumber(lead.detail_data[lead.preferred_contact]?.number)}`
                                                                : ""
                                                    }
                                                </span>
                                            )}
                                        </div>
                                        <div className='contact-nature' onClick={(e) => handleOpenNatureModal(e, lead?.contact_id, lead?.contact_nature, lead?.contact_nature_description)} style={{ cursor: 'pointer' }}>
                                            {Number(lead?.contact_nature) === 1 ? <Smile size={18} className="contact-nature-icon well-natured" /> : Number(lead?.contact_nature) === 2 ? <Meh size={18} className="contact-nature-icon neutral-natured" /> : Number(lead?.contact_nature) === 3 ? <Frown size={18} className="contact-nature-icon ill-natured" /> : <SmilePlus size={18} className="contact-nature-icon subtext" />}
                                        </div>
                                    </td>
                                    {displayHeaders.assigned_to && (
                                        <td style={{ ...leadColumnStyle, padding: '8px' }} title={lead.assigned_to}>
                                            {lead.assigned_to}
                                        </td>
                                    )}
                                    {displayHeaders.assigned_to_attorney && (
                                        <td style={{ ...leadColumnStyle, padding: '8px' }} title={lead.assigned_to_attorney}>
                                            {lead.assigned_to_attorney}
                                        </td>
                                    )}
                                    <td style={{ ...leadStatusColumnStyle, padding: '8px', textAlign: 'center' }} title={lead.status_name}>
                                        {lead.status_name}
                                    </td>
                                    {displayHeaders.created_at && (
                                        <td style={{ ...leadColumnStyle, padding: '8px' }} title={lead.created_at}>
                                            {getDaysSinceCreation(lead.created_at)}
                                        </td>
                                    )}
                                    {displayHeaders.case_type && (
                                        <td style={{ ...leadColumnStyle, padding: '8px' }} title={lead.case_type_name}>
                                            {lead.case_type_name}
                                        </td>
                                    )}
                                    {displayHeaders.marketing_source && (
                                        <td style={{ ...leadColumnStyle, padding: '8px' }} title={lead?.marketing_source_name}>
                                            <div style={{ textAlign: 'center', textWrap: 'wrap', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', flexWrap: 'wrap' }}>
                                                {lead?.marketing_source_name}
                                                {lead?.referral_contact && (
                                                    <span className='subtext'>({lead?.referral_contact})</span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    {displayHeaders.case_likelihood && (
                                        <td style={{ ...leadColumnStyle, padding: '8px', textAlign: 'center' }} title={`${lead.case_likelihood} - ${caseLikelihood[lead.case_likelihood - 1]}`}>
                                            {Array.from({ length: lead.case_likelihood }, (_, index) => (
                                                <StarSharp key={index} className="star-icon" />
                                            ))}
                                            {Array.from({ length: 5 - lead.case_likelihood }, (_, index) => (
                                                <StarOutlineSharp key={index} className="star-icon empty" />
                                            ))}
                                        </td>
                                    )}
                                    {displayHeaders.office && (
                                        <td style={{ ...leadColumnStyle, padding: '8px' }} title={lead.office_name}>
                                            {lead.office_name}
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="no-data">No leads found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {createLead && (
                <CreateLead 
                    user={user} 
                    fetchLeads={() => fetchLeads(pagination.current_page, pagination.per_page)} 
                    createLead={createLead} 
                    setCreateLead={setCreateLead}
                />
            )}
            {showNatureModal && (
                <Modal
                    title="Contact Nature"
                    onClose={() => setShowNatureModal(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button
                                onClick={() => setShowNatureModal(false)}
                                className="action alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveNature}
                                className="action"
                                disabled={selectedNature === null}
                            >
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        <div className='smiles-container'>
                            <div
                                onClick={() => setSelectedNature(1)}
                                className={`smile-container ${selectedNature === 1 ? 'selected' : ''}`}
                            >
                                <Smile size={30} className="contact-nature-icon well-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Well Natured</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(2)}
                                className={`smile-container ${selectedNature === 2 ? 'selected' : ''}`}
                            >
                                <Meh size={30} className="contact-nature-icon neutral-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Neutral</div>
                            </div>
                            <div
                                onClick={() => setSelectedNature(3)}
                                className={`smile-container ${selectedNature === 3 ? 'selected' : ''}`}
                            >
                                <Frown size={30} className="contact-nature-icon ill-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Ill Natured</div>
                            </div>
                        </div>

                        <div className='form-group full' style={{ maxWidth: '100%', width: '100%' }}>
                            <label className="subtext">
                                Nature Description
                            </label>
                            <textarea
                                value={natureDescription}
                                onChange={(e) => setNatureDescription(e.target.value)}
                                placeholder="Add notes about this contact's nature..."
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {message.message.length > 0 && (
                <Alert
                    message={message.message}
                    type={message.type}
                    onClose={() => setMessage({ message: '', type: '' })}
                />
            )}
        </>
    );
};